import { useState, useCallback, useRef } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { toast } from 'sonner'
import { Upload } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'
import { NativeSelect as Select } from '../../../components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { chameleon } from '@/lib/chameleon'
import { uploadCSVFile } from '../api'
import { useCapability } from '@/features/permissions/hooks'
import { useConfiguration } from '../../../hooks/useConfiguration'

// One capability per entity, mirroring IMPORT_CAPABILITY on the server: who
// may load appointments is not who may load patient records. This replaced a
// single `features.data_import` flag, which could say neither which entity nor
// which role.
const IMPORT_CAPABILITY: Record<DataEntity, string> = {
  client: 'data.clients.import',
  care_gap: 'data.caregap.import',
  appointment: 'data.appointments.import',
  order: 'data.order.import',
  provider: 'data.providers.import',
  timeslot: 'data.timeslot.import',
  visit: 'data.visits.import',
}
import {
  DATA_SOURCES,
  FILE_NAME_HINTS,
  MAX_FILE_SIZE_BYTES,
  type DataSource,
  type DataEntity,
} from '../types'
import { useListConfigs } from '../../../hooks/useConfigAdmin'

interface ImportDataButtonProps {
  entity: DataEntity
  entityLabel: string
  onSuccess?: () => void
}

export function ImportDataButton({
  entity,
  entityLabel,
  onSuccess,
}: ImportDataButtonProps) {
  const { getToken } = useAuth()
  const canImportCrossOrg = useCapability('data.import.crossorg') === true
  const { data: orgConfigs } = useListConfigs(canImportCrossOrg)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { configuration } = useConfiguration()
  const dataImportEnabled =
    useCapability(IMPORT_CAPABILITY[entity]) === true &&
    (configuration?.features?.data_import ?? false)

  const availableOrgs = orgConfigs?.map((config) => config.org_slug) ?? []
  const showOrgSelector = canImportCrossOrg && availableOrgs.length > 0

  const [isOpen, setIsOpen] = useState(false)
  const [source, setSource] = useState<DataSource | null>(null)
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const resetForm = useCallback(() => {
    setSource(null)
    setSelectedOrg(null)
    setFile(null)
    setFileError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!isUploading) {
        setIsOpen(open)
        if (!open) {
          resetForm()
        }
      }
    },
    [resetForm, isUploading]
  )

  const handleSourceChange = (value: string) => {
    setSource(value ? (value as DataSource) : null)
  }

  const handleOrgChange = (value: string) => {
    setSelectedOrg(value || null)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null
    setFile(selectedFile)
    setFileError(null)
  }

  const validateFile = useCallback((): boolean => {
    if (!file) {
      setFileError('Please select a file')
      return false
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileError('Only CSV files are allowed')
      return false
    }
    if (file.size === 0) {
      setFileError('File cannot be empty')
      return false
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('File size must be less than 30MB')
      return false
    }
    return true
  }, [file])

  const handleSubmit = useCallback(async () => {
    if (!source || !validateFile()) {
      return
    }

    setIsUploading(true)

    try {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }

      await uploadCSVFile(
        file!,
        entity,
        source,
        token,
        selectedOrg ?? undefined
      )

      chameleon.track('Data Imported')

      toast.success(
        'File uploaded successfully. Data will be processed in the background.'
      )

      setIsOpen(false)
      resetForm()
      onSuccess?.()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to upload file'
      toast.error(message)
    } finally {
      setIsUploading(false)
    }
  }, [
    file,
    entity,
    source,
    selectedOrg,
    getToken,
    validateFile,
    resetForm,
    onSuccess,
  ])

  if (!dataImportEnabled) {
    return null
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Import
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-4">
        <div className="space-y-4">
          <h3 className="font-semibold text-sm">Import {entityLabel} Data</h3>

          {showOrgSelector && (
            <div className="space-y-1.5">
              <Label htmlFor="import-org" className="text-xs">
                Organization
              </Label>
              <Select
                id="import-org"
                value={selectedOrg || ''}
                onChange={(e) => handleOrgChange(e.target.value)}
                disabled={isUploading}>
                <option value="">Current organization</option>
                {availableOrgs.map((org) => (
                  <option key={org} value={org}>
                    {org}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="import-source" className="text-xs">
              Source
            </Label>
            <Select
              id="import-source"
              required
              value={source || ''}
              onChange={(e) => handleSourceChange(e.target.value)}
              disabled={isUploading}>
              {DATA_SOURCES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
            {source && FILE_NAME_HINTS[entity]?.[source] && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5 mt-1.5">
                <span className="font-medium">Look for files like:</span>
                <ul className="mt-1 space-y-0.5">
                  {FILE_NAME_HINTS[entity]![source]!.map((hint, i) => (
                    <li key={i} className="break-words">
                      {hint}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="import-file" className="text-xs">
              CSV File
            </Label>
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                fileError
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300 hover:border-gray-400'
              } ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => !isUploading && fileInputRef.current?.click()}>
              <input
                ref={fileInputRef}
                id="import-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading}
              />
              <Upload className="mx-auto h-6 w-6 text-gray-400 mb-1" />
              {file ? (
                <div>
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-gray-600">
                    Click to select a CSV file
                  </p>
                  <p className="text-xs text-gray-500">Maximum 30MB</p>
                </div>
              )}
            </div>
            {fileError && <p className="text-xs text-red-500">{fileError}</p>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
              disabled={isUploading}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isUploading || !file || !source}>
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
